import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  ApiGeometryType,
  Geometry,
  LatLong,
  Line,
  Point,
  PointColors,
  Polygon,
} from '../../models/Geometries';
import { GeometryService } from '../services/map/geometry.service';
import { lineString, point, polygon } from '@turf/helpers';
import { ApiGeometryUtils } from '../../utils/ApiGeometryUtils';
import { FeatureGeometry } from '../../models/Geoserver';

export interface MapState {
  mapTutorial: boolean;
  points: Point[];
  lines: Line[];
  polygons: Polygon[];
  syncPoints: Point[],
  syncLines: Line[],
  syncPolygons: Polygon[],
  addingGeometry: boolean;
  addingGeomtryType: 'punto' | 'linea' | 'poligono';
  geometrySelected?: Geometry;
  geometrySyncSelected?: Polygon | Line | Point;
  currentLine: LatLong[];
  pointOfLineSelected: number;
  editingGeometryId?: number;
  editingGeometry?: Geometry;
  error?: string;
  userLocation?: LatLong;
  allGeometries: FeatureGeometry[];
}

const initialState: MapState = {
  mapTutorial: false,
  points: [],
  lines: [],
  polygons: [],
  syncPoints: [],
  syncLines: [],
  syncPolygons: [],
  addingGeometry: false,
  addingGeomtryType: 'punto',
  currentLine: [],
  pointOfLineSelected: 0,
  allGeometries: [],
};

export const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    resetMap: (state: MapState) => {
      state.mapTutorial = false
      state.points = []
      state.lines = []
      state.polygons = []
      state.syncPoints = []
      state.syncLines = []
      state.syncPolygons = []
      state.addingGeometry = false;
      state.geometrySelected = undefined
      state.geometrySyncSelected = undefined
      state.currentLine = []
      state.editingGeometryId = undefined
      state.editingGeometry = undefined
      state.allGeometries = [];
    },
    openMapTutorial: (state: MapState) => {
      state.mapTutorial = !localStorage.getItem('mapTutorialShown');
      state.addingGeometry = true;
      state.addingGeomtryType = 'punto';
      state.currentLine = [];
      state.geometrySelected = undefined;
      state.geometrySyncSelected = undefined
    },
    startAddingGeometry: (state: MapState) => {
      state.addingGeometry = true;
    },
    startAddingLine: (state: MapState) => {
      state.editingGeometryId = undefined;
      state.editingGeometry = undefined;
      state.addingGeometry = true;
      state.addingGeomtryType = 'linea';
      state.currentLine = [];
      state.pointOfLineSelected = -1;
      state.geometrySelected = undefined;
      state.geometrySyncSelected = undefined;
    },
    startAddingPolygon: (state: MapState) => {
      state.editingGeometryId = undefined;
      state.editingGeometry = undefined;
      state.addingGeometry = true;
      state.addingGeomtryType = 'poligono';
      state.currentLine = [];
      state.pointOfLineSelected = -1;
      state.geometrySelected = undefined;
      state.geometrySyncSelected = undefined;
    },
    cancelAddingGeometry: (state: MapState) => {
      if (state.editingGeometryId) {
        const arrayName =
          state.addingGeomtryType === 'punto'
            ? 'points'
            : state.addingGeomtryType === 'linea'
            ? 'lines'
            : 'polygons';
        state[arrayName].push(state.editingGeometry as any);
        state.geometrySelected = state.editingGeometry;
      }
      state.addingGeometry = false;
      state.editingGeometryId = undefined;
      state.editingGeometry = undefined;
      state.currentLine = [];
    },
    selectGeometry: (state: MapState, arg: PayloadAction<Geometry>) => {
      state.geometrySelected = arg.payload;
    },
    //Se selecciona el punto sincronizado
    selectSyncGeometry: (state: MapState, arg: PayloadAction<Point | Line | Polygon>) => {
      state.geometrySyncSelected = arg.payload;
    },
    selectLineId: (state: MapState, arg: PayloadAction<number>) => {
      state.geometrySelected = state.lines.find((l) => l.id === arg.payload);
    },
    selectPolygonId: (state: MapState, arg: PayloadAction<number>) => {
      state.geometrySelected = state.polygons.find((l) => l.id === arg.payload);
    },
    //Se selecciona la linea sincronizada
    selectSyncLineId: (state: MapState, arg: PayloadAction<number>) => {
      state.geometrySyncSelected = state.syncLines.find((l) => l.id === arg.payload);
    },
    //Se selecciona el poligono sincronizada
    selectSyncPolygonId: (state: MapState, arg: PayloadAction<number>) => {
      state.geometrySyncSelected = state.syncPolygons.find((l) => l.id === arg.payload);
    },
    unselectGeometry: (state: MapState) => {
      state.geometrySelected = undefined;
    },
    // se desselecciona la geometria sincronizada
    unselectSyncGeometry: (state: MapState) => {
      state.geometrySyncSelected = undefined;
    },
    closeMapTutorial: (state: MapState, arg: PayloadAction<boolean>) => {
      const dontShowAgain = arg.payload;
      if (dontShowAgain) localStorage.setItem('mapTutorialShown', 'true');
      state.mapTutorial = false;
    },
    addPointToLine: (state: MapState, arg: PayloadAction<LatLong>) => {
      state.currentLine.push(arg.payload);
    },
    selectPointOfLine: (state: MapState, arg: PayloadAction<number>) => {
      state.pointOfLineSelected = arg.payload;
    },
    modifyPointOfLine: (
      state: MapState,
      arg: PayloadAction<{ index: number; latLong: LatLong }>,
    ) => {
      state.currentLine[arg.payload.index] = arg.payload.latLong;
    },
    addMiddlepoint: (
      state: MapState,
      arg: PayloadAction<{ index: number; latLong: LatLong }>,
    ) => {
      state.currentLine.splice(arg.payload.index + 1, 0, arg.payload.latLong);
    },
    deletePointOfLineSelected: (state: MapState) => {
      if (state.pointOfLineSelected > -1) {
        state.currentLine.splice(state.pointOfLineSelected, 1);
        if (state.pointOfLineSelected > 0) {
          state.pointOfLineSelected--;
        }
      }
    },
    editExistingLine: (
      state: MapState,
      arg: PayloadAction<{ id: number; type: 'linea' | 'poligono' }>,
    ) => {
      const arrayName = arg.payload.type === 'linea' ? 'lines' : 'polygons';
      const index = state[arrayName].findIndex((l) => l.id === arg.payload.id);
      if (index === -1) return;

      const [geometry] = state[arrayName].splice(index, 1);
      state.addingGeomtryType = arg.payload.type;
      state.addingGeometry = true;
      state.currentLine = [...geometry.puntos];
      if (arg.payload.type === 'poligono') {
        state.currentLine.pop();
      }
      state.editingGeometryId = geometry.id;
      state.editingGeometry = geometry;
      state.geometrySelected = undefined;
    },
    updateUserLocation: (state: MapState, arg: PayloadAction<LatLong>) => {
      state.userLocation = arg.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(addPoint.fulfilled, (state, action) => {
      const point: Point = {
        latitud: action.meta.arg.latitud,
        longitud: action.meta.arg.longitud,
        id: action.payload.id,
        descripcion: '',
        color: PointColors[0],
        fechaCreacion: new Date().getTime(),
        tipo: 'punto',
        titulo: 'Punto ' + (state.points.length + 1),
        createdBy: localStorage.getItem('userName')!,
      };
      state.points.push(point);
      state.addingGeometry = false;
      state.geometrySelected = point;
    })
    builder.addCase(loadGeometries.fulfilled, (state, action) => {
      state.lines = [];
      state.polygons = [];
      state.points = [];
      state.syncLines = [];
      state.syncPolygons = [];
      state.syncPoints = [];
      state.allGeometries = action.payload.map(g => g.geometria);
      action.payload.forEach(apiGeometry => {
        if(apiGeometry.geometria.type === 'Point' && apiGeometry.tipo === ApiGeometryType.USER_DRAWN) {
          state.points.push(ApiGeometryUtils.toUserDrawnPoint(apiGeometry))
        }
        if(apiGeometry.geometria.type === 'Polygon'  && apiGeometry.tipo === ApiGeometryType.USER_DRAWN) {
          state.polygons.push(ApiGeometryUtils.toUserDrawnPolygon(apiGeometry))
        }
        if(apiGeometry.geometria.type === 'LineString'  && apiGeometry.tipo === ApiGeometryType.USER_DRAWN) {
          state.lines.push(ApiGeometryUtils.toUserDrawnLine(apiGeometry))
        }
        //Geometrias ya sincronizadas
        if(apiGeometry.geometria.type === 'Point' && apiGeometry.tipo === ApiGeometryType.INSPECTOR_DRAWN) {
          state.syncPoints.push(ApiGeometryUtils.toUserDrawnPoint(apiGeometry))
        }
        if(apiGeometry.geometria.type === 'Polygon'  && apiGeometry.tipo === ApiGeometryType.INSPECTOR_DRAWN) {
          state.syncPolygons.push(ApiGeometryUtils.toUserDrawnPolygon(apiGeometry))
        }
        if(apiGeometry.geometria.type === 'LineString'  && apiGeometry.tipo === ApiGeometryType.INSPECTOR_DRAWN) {
          state.syncLines.push(ApiGeometryUtils.toUserDrawnLine(apiGeometry))
        }
        //************************* */
      })
    });
    builder.addCase(deleteGeometry.fulfilled, (state) => {
      const arrayName =
        state.geometrySelected?.tipo === 'punto'
          ? 'points'
          : state.geometrySelected?.tipo === 'linea'
            ? 'lines'
            : 'polygons';
      const index = state[arrayName].findIndex(
        (p) => p.id === state.geometrySelected?.id,
      );
      const arr = [...state[arrayName]];
      arr.splice(index, 1);
      state[arrayName] = arr as any;
      state.geometrySelected = undefined;
    });
      builder.addCase(editGeometry.pending, (state) => {
        state.error = undefined
      });
      builder.addCase(editGeometry.rejected, (state,action) => {
        const errorMessage: string = (action.payload as any).error;
        if(/Key \(titulo, tarea_id\)=[\s\S]* already exists./.test(errorMessage)){
          state.error = 'Existe en la tarea otra geometría con el mismo título'
        } else {
          state.error = 'Error al actualizar geometria'
        }
      });
      builder.addCase(editGeometry.fulfilled, (state,action) => {
      state.geometrySelected = {
        ...state.geometrySelected!,
        color: action.meta.arg.color,
        titulo: action.meta.arg.title,
        descripcion: action.meta.arg.description,
      };
      const arrayName =
        state.geometrySelected.tipo === 'punto'
          ? 'points'
          : state.geometrySelected.tipo === 'linea'
            ? 'lines'
            : 'polygons';
      const arr = [...state[arrayName]];
      const index = state[arrayName].findIndex(
        (p) => p.id === state.geometrySelected!.id,
      );
      arr[index] = { ...state.geometrySelected! } as any;
      state[arrayName] = arr as any;
    })
    builder.addCase(saveCurrentShape.fulfilled, (state, action) => {
      const minPoints = state.addingGeomtryType === 'linea' ? 2 : 3;
      const arrayName =
        state.addingGeomtryType === 'linea' ? 'lines' : 'polygons';
      const geometryName =
        state.addingGeomtryType === 'linea' ? 'Línea ' : 'Polígono ';
      if (
        state.addingGeometry &&
        state.addingGeomtryType !== 'punto' &&
        state.currentLine.length >= minPoints
      ) {
        const last = state.currentLine.length - 1;
        if (
          state.addingGeomtryType === 'poligono' &&
          state.currentLine[0].latitud !== state.currentLine[last].latitud &&
          state.currentLine[0].longitud !== state.currentLine[last].longitud
        ) {
          state.currentLine.push({ ...state.currentLine[0] });
        }

        const geometry: Line | Polygon = {
          id: state.editingGeometry
            ? state.editingGeometry.id
            : action.payload!.id,
          puntos: [...state.currentLine],
          descripcion: state.editingGeometry
            ? state.editingGeometry.descripcion
            : undefined,
          color: state.editingGeometry
            ? state.editingGeometry.color
            : PointColors[0],
          fechaCreacion: state.editingGeometry
            ? state.editingGeometry.fechaCreacion
            : new Date().getTime(),
          tipo: state.editingGeometry
            ? state.editingGeometry.tipo
            : state.addingGeomtryType,
          titulo: state.editingGeometry
            ? state.editingGeometry.titulo
            : geometryName + (state[arrayName].length + 1),
          createdBy: state.editingGeometry
            ? state.editingGeometry.createdBy
            : localStorage.getItem('userName')!,
        };
        state[arrayName].push(geometry as any);
        state.addingGeometry = false;
        state.geometrySelected = geometry;
        state.currentLine = [];
        state.pointOfLineSelected = -1;
        state.editingGeometry = undefined;
        state.editingGeometryId = undefined;
      }
    })
  }
});

export const addPoint = createAsyncThunk('add_point', async (latLong: LatLong, thunkApi) => {
  return await GeometryService.saveGeometry({
    titulo: 'Punto ' + (thunkApi.getState() as RootState).map.points.length + 1,
    tipo: ApiGeometryType.USER_DRAWN,
    atributos: {},
    descripcion: undefined,
    geometria: point([latLong.longitud, latLong.latitud]).geometry,
    tareaId: (thunkApi.getState() as RootState).task.currentTask!.id!
  });
});

export const editGeometry = createAsyncThunk('edit_geometry',
  async (payload: { title: string; description?: string; color: string }, thunkApi) => {

  try {
    return await GeometryService.updateGeometry({
      titulo: payload.title,
      descripcion: payload.description ? payload.description : undefined,
      atributos: {
        style: {
          color: payload.color
        }
      },
    }, (thunkApi.getState() as RootState).map.geometrySelected!.id);
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response.data)
  }

});

export const saveCurrentShape = createAsyncThunk('save_current_shape', async (_: void, thunkApi) => {
  const state: MapState = (thunkApi.getState() as RootState).map;

  if(state.editingGeometry) {
      return await GeometryService.updateGeometry({
        geometria: state.addingGeomtryType === 'linea' ?  lineString(state.currentLine.map(p => [p.longitud, p.latitud])).geometry :
          polygon([[
            ...state.currentLine.map(p => [p.longitud, p.latitud]),
            [state.currentLine[0].longitud, state.currentLine[0].latitud]
          ]]).geometry,
      }, state.editingGeometryId!)
  }

  if(state.addingGeomtryType === 'linea') {
     return await GeometryService.saveGeometry({
        titulo: 'Línea ' + (thunkApi.getState() as RootState).map.lines.length + 1,
        tipo: ApiGeometryType.USER_DRAWN,
        atributos: {
          style: {
            color: PointColors[0]
          }
        },
        geometria: lineString(state.currentLine.map(p => [p.longitud, p.latitud])).geometry,
        tareaId: (thunkApi.getState() as RootState).task.currentTask!.id!
      })
  } else {
    return await GeometryService.saveGeometry({
      titulo: 'Polígono ' + (thunkApi.getState() as RootState).map.polygons.length + 1,
      tipo: ApiGeometryType.USER_DRAWN,
      atributos: {
        style: {
          color: PointColors[0]
        }
      },
      geometria: polygon([
        state.currentLine.map(p => [p.longitud, p.latitud])
      ]).geometry,
      tareaId: (thunkApi.getState() as RootState).task.currentTask!.id!
    })
  }
});


export const loadGeometries = createAsyncThunk('load_geometries', async (_: void, thunkApi) => {
  return await GeometryService.getGeometries((thunkApi.getState() as RootState).task.currentTask!.id!)
});

export const deleteGeometry = createAsyncThunk('delete_geometry', async (_: void, thunkApi) => {
  return await GeometryService.deleteGeometry((thunkApi.getState() as RootState).map.geometrySelected!.id!)
});


export const {
  openMapTutorial,
  closeMapTutorial,
  cancelAddingGeometry,
  startAddingGeometry,
  selectGeometry,
  unselectGeometry,
  selectSyncGeometry,
  unselectSyncGeometry,
  startAddingLine,
  addPointToLine,
  selectPointOfLine,
  modifyPointOfLine,
  selectLineId,
  deletePointOfLineSelected,
  editExistingLine,
  addMiddlepoint,
  startAddingPolygon,
  selectPolygonId,
  selectSyncLineId,
  selectSyncPolygonId,
  resetMap,
  updateUserLocation,
} = mapSlice.actions;

export const selectMapTutorial = (state: RootState) => state.map.mapTutorial;
export const selectAddingGeometry = (state: RootState) =>
  state.map.addingGeometry;
export const selectAddingGeometryType = (state: RootState) =>
  state.map.addingGeomtryType;
export const selectGeometrySelected = (state: RootState) =>
  state.map.geometrySelected;
export const selectSyncGeometrySelected = (state: RootState) =>
  state.map.geometrySyncSelected;
export const selectPoints = (state: RootState) => state.map.points;
export const selectSyncPoints = (state: RootState) => state.map.syncPoints;
export const selectCurrentLine = (state: RootState) => state.map.currentLine;
export const selectPointOfLineSelected = (state: RootState) =>
  state.map.pointOfLineSelected;
export const selectLines = (state: RootState) => state.map.lines;
export const selectSyncLines = (state: RootState) => state.map.syncLines;
export const selectPolygons = (state: RootState) => state.map.polygons;
export const selectSyncPolygons = (state: RootState) => state.map.syncPolygons;
export const selectEditingGeometryId = (state: RootState) =>
  state.map.editingGeometryId;
export const selectEditingGeometry = (state: RootState) =>
  state.map.editingGeometry;
export const selectMapError = (state: RootState) => state.map.error;
export default mapSlice.reducer;
